import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/money";
import {
  BarChart, Bar, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import {
  TrendingUp, Users, HeartHandshake, Timer, Trophy, Eye, RefreshCw, CreditCard, Activity, PieChart as PieIcon, AlertTriangle, BellRing, Save, Wallet,
} from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const MONTHS_FR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const shortMonth = (ym) => {
  const [, m] = String(ym).split("-");
  return MONTHS_FR[(parseInt(m, 10) || 1) - 1] || ym;
};

function Metric({ icon: Icon, label, value, sub, tone = "indigo", testid }) {
  const tones = {
    indigo: "bg-findo-indigo/10 text-findo-indigo",
    emerald: "bg-emerald-500/10 text-emerald-600",
    red: "bg-findo-red/10 text-findo-red",
    amber: "bg-amber-500/10 text-amber-600",
    slate: "bg-slate-500/10 text-slate-600",
  };
  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <div className={`h-9 w-9 grid place-items-center rounded-xl ${tones[tone]}`}><Icon size={18} /></div>
        <p className="text-sm text-slate-500 font-medium">{label}</p>
      </div>
      <p className="font-display text-3xl font-extrabold text-findo-navy mt-3">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function ChartCard({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-6" data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-5">
        <div className="h-9 w-9 grid place-items-center rounded-xl bg-findo-navy/5 text-findo-navy"><Icon size={18} /></div>
        <h3 className="font-display font-bold text-lg text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

const PERIODS = [3, 6, 12];

export default function GlobalStatsPanel() {
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [months, setMonths] = useState(6);
  const [threshold, setThreshold] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);

  async function load(m = months) {
    setBusy(true);
    try {
      const [d, r] = await Promise.all([
        api(`/admin/stats?months=${m}`),
        api(`/admin/revenue?months=${m}`).catch(() => null),
      ]);
      setData(d);
      setRevenue(r);
      setThreshold(String(d?.churn_30d?.threshold ?? ""));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(months); }, [months]);

  async function saveThreshold() {
    const val = parseInt(threshold, 10);
    if (!val || val < 1 || val > 100) { toast.error("Seuil entre 1 et 100"); return; }
    setSavingThreshold(true);
    try {
      await api("/admin/churn-alert-settings", { method: "PUT", body: { threshold: val } });
      toast.success(`Seuil d'alerte churn réglé à ${val}%`);
      await load(months);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingThreshold(false);
    }
  }

  if (!data) {
    return (
      <div className="bg-white rounded-2xl border border-border card-shadow p-10 grid place-items-center text-slate-400" data-testid="stats-loading">
        <FindoSpinner size={22} />
      </div>
    );
  }

  const s = data.subscriptions || {};
  const subBars = [
    { name: "Starter EU", value: s.starter_europe || 0 },
    { name: "Business EU", value: s.business_europe || 0 },
    { name: "Starter AF", value: s.starter_afrique || 0 },
    { name: "Business AF", value: s.business_afrique || 0 },
  ];
  const subColors = ["#4f46e5", "#7c3aed", "#0ea5e9", "#0891b2"];
  const newMonthly = (data.monthly_new || []).map((m) => ({ ...m, label: shortMonth(m.month) }));
  const traffic = (data.traffic_monthly || []).map((m) => ({ ...m, label: shortMonth(m.month) }));
  const topPartners = data.top_partners || [];

  const churn = data.churn_30d || {};

  return (
    <div className="space-y-6" data-testid="global-stats-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Pilotage stratégique — croissance, santé du réseau, performance opérationnelle et trafic.</p>
        <div className="flex items-center gap-3">
          <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="stats-period-toggle">
            {PERIODS.map((m) => (
              <button key={m} onClick={() => setMonths(m)} data-testid={`stats-period-${m}`}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors ${months === m ? "bg-white text-findo-navy card-shadow" : "text-slate-500 hover:text-slate-700"}`}>
                {m} mois
              </button>
            ))}
          </div>
          <button onClick={() => load(months)} disabled={busy} className="inline-flex items-center gap-1.5 text-sm font-semibold text-findo-indigo hover:opacity-80" data-testid="stats-refresh-btn">
            {busy ? <FindoSpinner size={15} /> : <RefreshCw size={15} />} Actualiser
          </button>
        </div>
      </div>

      {churn.alert && (
        <div className="rounded-2xl border-2 border-findo-red bg-findo-red/5 p-4 flex items-start gap-3" data-testid="churn-alert-banner">
          <AlertTriangle size={20} className="text-findo-danger shrink-0 mt-0.5" />
          <p className="text-sm text-findo-red font-semibold">
            Churn 30 jours élevé : <b>{churn.rate}%</b> dépasse le seuil d'alerte ({churn.threshold}%).
            Une notification superadmin a été envoyée.
          </p>
        </div>
      )}

      {/* Croissance */}
      <h2 className="font-display font-bold text-findo-navy text-sm uppercase tracking-wide">Croissance</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={TrendingUp} label="Abonnements actifs" value={s.total_active || 0} sub="Formules payantes (Starter + Business)" tone="indigo" testid="stat-active-subs" />
        <Metric icon={CreditCard} label="MRR estimé" value={formatMoney(data.mrr_eur || 0, "fr")} sub="Revenu mensuel récurrent" tone="emerald" testid="stat-mrr" />
        <Metric icon={Activity} label="Churn 30 j" value={`${data.churn_30d?.rate ?? 0} %`} sub={`${data.churn_30d?.inactive_paid ?? 0} compte(s) payant(s) inactif(s)`} tone={churn.alert ? "red" : "indigo"} testid="stat-churn" />
        <Metric icon={Users} label="Utilisateurs totaux" value={data.users?.total ?? 0} sub={`${data.users?.b2b ?? 0} B2B · ${data.users?.b2c ?? 0} B2C`} tone="slate" testid="stat-users" />
      </div>

      {/* Réglage du seuil d'alerte churn */}
      <div className="bg-white rounded-2xl border border-border card-shadow p-5 flex flex-wrap items-center gap-4" data-testid="churn-threshold-card">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 grid place-items-center rounded-xl bg-findo-red/10 text-findo-red"><BellRing size={18} /></div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Alerte churn</p>
            <p className="text-xs text-slate-400">Notification + SMS superadmin quand le churn 30 j dépasse le seuil.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-slate-500">Seuil</span>
          <input type="number" min={1} max={100} value={threshold} onChange={(e) => setThreshold(e.target.value)}
            className="w-20 rounded-xl border border-border px-3 py-1.5 text-sm font-semibold text-findo-navy focus:outline-none focus:ring-2 focus:ring-findo-indigo/30"
            data-testid="churn-threshold-input" />
          <span className="text-sm text-slate-500">%</span>
          <button onClick={saveThreshold} disabled={savingThreshold}
            className="inline-flex items-center gap-1.5 rounded-xl bg-findo-navy text-white px-4 py-1.5 text-sm font-semibold hover:bg-findo-navy/90 disabled:opacity-60"
            data-testid="churn-threshold-save">
            {savingThreshold ? <FindoSpinner size={15} /> : <Save size={15} />} Enregistrer
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard icon={PieIcon} title="Abonnements par type" testid="chart-subs">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={subBars} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <RTooltip cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {subBars.map((_, i) => <Cell key={i} fill={subColors[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard icon={TrendingUp} title="Nouveaux abonnés / mois" testid="chart-new-subs">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={newMonthly} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <RTooltip cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="count" fill="#4f46e5" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Revenus encaissés par source */}
      {revenue && (
        <>
          <h2 className="font-display font-bold text-findo-navy text-sm uppercase tracking-wide">
            Revenus encaissés — {revenue.months} derniers mois
          </h2>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-border card-shadow p-6" data-testid="revenue-breakdown">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="h-9 w-9 grid place-items-center rounded-xl bg-emerald-500/10 text-emerald-600"><Wallet size={18} /></div>
                <h3 className="font-display font-bold text-lg text-slate-900">Par source</h3>
                <span className="ml-auto text-right">
                  <span className="block font-display text-2xl font-extrabold text-findo-navy" data-testid="revenue-total">{formatMoney(revenue.total_eur || 0, "fr")}</span>
                  <span className="block text-xs text-slate-400">{revenue.total_count || 0} paiement(s)</span>
                </span>
              </div>
              {(revenue.sources || []).length === 0 ? (
                <p className="text-sm text-slate-400">Aucun paiement encaissé sur la période.</p>
              ) : (
                <ul className="space-y-2.5" data-testid="revenue-sources-list">
                  {revenue.sources.map((src) => {
                    const pct = revenue.total_eur > 0 ? Math.round((src.total_eur / revenue.total_eur) * 100) : 0;
                    return (
                      <li key={src.key} className="flex items-center gap-3" data-testid={`revenue-source-${src.key}`}>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center justify-between text-sm">
                            <span className="font-medium text-slate-700 truncate">{src.label_fr}</span>
                            <span className="font-bold text-findo-navy ml-3">{formatMoney(src.total_eur, "fr")}</span>
                          </span>
                          <span className="mt-1 flex items-center gap-2">
                            <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                            </span>
                            <span className="text-[11px] text-slate-400 w-16 text-right">{pct}% · {src.count}×</span>
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <ChartCard icon={TrendingUp} title="Revenus / mois (€)" testid="chart-revenue-monthly">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={(revenue.monthly || []).map((m) => ({ ...m, label: shortMonth(m.month) }))} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <RTooltip cursor={{ fill: "#f8fafc" }} formatter={(v) => [`${v} €`, "Revenus"]} />
                  <Bar dataKey="total_eur" fill="#059669" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}

      {/* Santé du réseau */}
      <h2 className="font-display font-bold text-findo-navy text-sm uppercase tracking-wide">Santé du réseau</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Metric icon={HeartHandshake} label="Taux de rétention B2B" value={`${data.retention?.rate ?? 0} %`} sub={`${data.retention?.active_30d ?? 0}/${data.retention?.total ?? 0} partenaires actifs (30 j)`} tone="emerald" testid="stat-retention" />
        <Metric icon={Trophy} label="Réussite des restitutions" value={`${data.success_rate?.rate ?? 0} %`} sub={`${data.success_rate?.restituted ?? 0}/${data.success_rate?.total ?? 0} objets restitués`} tone="amber" testid="stat-success" />
        <Metric icon={Timer} label="Délai moyen de restitution" value={data.avg_restitution_days == null ? "—" : `${data.avg_restitution_days} j`} sub="Entre déclaration et paiement" tone="indigo" testid="stat-avg-delay" />
      </div>

      {/* Performance opérationnelle */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard icon={Trophy} title="Top 5 partenaires les plus actifs" testid="chart-top-partners">
          {topPartners.length === 0 ? (
            <p className="text-sm text-slate-400">Aucune donnée.</p>
          ) : (
            <ul className="space-y-2.5" data-testid="top-partners-list">
              {topPartners.map((p, i) => (
                <li key={p.partner_id} className="flex items-center gap-3" data-testid={`top-partner-${i}`}>
                  <span className="h-7 w-7 grid place-items-center rounded-lg bg-findo-indigo/10 text-findo-indigo font-bold text-xs shrink-0">{i + 1}</span>
                  <span className="flex-1 min-w-0 truncate text-sm font-medium text-slate-700">{p.name}</span>
                  <span className="text-sm font-bold text-findo-navy">{p.count} objets</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>

        <ChartCard icon={Eye} title="Trafic — vues des annonces / mois" testid="chart-traffic">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={traffic} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <RTooltip cursor={{ stroke: "#e2e8f0" }} />
              <Line type="monotone" dataKey="views" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
